import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { ColDef, GridApi, GridOptions, GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../services";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { GridViewBaseComponent } from "../utility/grid-view-base.component";
import { SharedDataService } from "../services/sharedDataService";
import { extendDefaultOptions, agDateColumnFilter } from "../utility/gridHelper";
import { PreprocessDateTime } from "../utility/view-field-mapping";
import { LinkCellComponent } from "../shared/grid/link-cell.component";
import { environment } from "src/environments/environment";
import { Blueprint } from "../generated-models/Blueprint";

/** Logical-key lineage rendered in the summary panel of the blueprint-details page. */
interface BlueprintLineage {
  ServiceTreeId: string;
  Type: string;
  Name: string;
}

/**
 *  Details page for a Blueprint — renders the lineage summary at the top and every historical
 *  version in a grid below.
 *
 *  Route contract: `/capacity/blueprint/:id` (redirects to logical-key form) or
 *  `/capacity/blueprint/serviceTreeId/:serviceTreeId/type/:type/name/:name?ID=<n>`.
 */
@Component({
  standalone: false,
  templateUrl: "./blueprint-details.component.html",
  styleUrls: ["../styles.scss", "./capacity-blueprint.scss"],
})
export class CapacityBlueprintDetailsComponent extends GridViewBaseComponent implements OnInit {
  gridOptions!: GridOptions<Record<string, unknown>>;
  gridApi!: GridApi;
  initPageSize = 20;
  keyFilterStats = "filter:capacity-blueprint-blueprint-details";

  entityId: number | null = null;
  lineage: BlueprintLineage | null = null;
  rowData: Record<string, unknown>[] = [];
  isLoading = false;
  isRefreshing = false;
  errorMessage: string | null = null;

  constructor(
    protected apiService: ApiService,
    protected sharedDataService: SharedDataService,
    private route: ActivatedRoute,
    private loadingService: LoadingScreenService,
    private notificationService: ToastrService,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
  ) {
    super(apiService, sharedDataService);
  }

  /** True when the lineage returned no versions — usually an invalid id. */
  get isEmpty(): boolean {
    return !this.isLoading && this.rowData.length === 0 && !this.errorMessage;
  }

  async ngOnInit(): Promise<void> {
    const paramMap = this.route.snapshot.paramMap;
    const queryMap = this.route.snapshot.queryParamMap;

    const idParam = paramMap.get("id");
    let serviceTreeId = paramMap.get("serviceTreeId");
    let type = paramMap.get("type");
    let name = paramMap.get("name");

    if (idParam != null) {
      const id = Number(idParam);
      if (Number.isNaN(id)) {
        this.errorMessage = `Invalid Blueprint id: '${idParam}'.`;
        return;
      }
      this.entityId = id;

      this.isLoading = true;
      this.loadingService.setLoading(true);
      try {
        const blueprint = await this.apiService.getCapacityBlueprint(id).toPromise();
        if (!blueprint) {
          this.errorMessage = `Blueprint with id '${id}' was not found.`;
          return;
        }
        serviceTreeId = blueprint.ServiceTreeId ?? null;
        type = blueprint.Type ?? null;
        name = blueprint.Name ?? null;
        if (!serviceTreeId || !type || !name) {
          this.errorMessage = "Blueprint is missing logical-key fields (ServiceTreeId/Type/Name).";
          return;
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        this.errorMessage = `Failed to load blueprint: ${message}`;
        this.notificationService.error(this.errorMessage);
        return;
      } finally {
        this.isLoading = false;
        this.loadingService.setLoading(false);
      }
    } else if (serviceTreeId && type && name) {
      const queryId = queryMap.get("ID");
      const parsedQueryId = queryId != null ? Number(queryId) : NaN;
      this.entityId = Number.isNaN(parsedQueryId) ? null : parsedQueryId;
    } else {
      this.errorMessage = "Missing route parameters for blueprint details.";
      return;
    }

    const titleSuffix = this.entityId ?? name;
    this.titleService.setTitle(`Blueprint ${titleSuffix} - Capacity Blueprint`.trim());

    // Only call the history endpoint when all logical-key fields are resolved.
    if (!serviceTreeId || !type || !name) {
      return;
    }

    await this.loadHistory(serviceTreeId, type, name);
  }

  onGridReady(params: GridReadyEvent) {
    super.onGridReady(params);
    this.gridApi = params.api;
    this.gridApi.hideOverlay();
    setTimeout(() => this.gridApi?.autoSizeAllColumns(), 100);
  }

  refreshData(): void {
    this.gridApi?.hideOverlay();
  }

  /**
   *  Re-reads this blueprint from the most recent commit that changed it, recording a new version when
   *  that commit differs from the latest stored one, then reloads the version history.
   */
  async refreshBlueprint(): Promise<void> {
    if (this.isRefreshing || !this.lineage) {
      return;
    }

    const { ServiceTreeId, Type, Name } = this.lineage;
    this.isRefreshing = true;
    this.loadingService.setLoading(true);
    try {
      const result = await this.apiService.refreshSingleBlueprint(ServiceTreeId, Type, Name).toPromise();
      await this.loadHistory(ServiceTreeId, Type, Name);
      this.notificationService.info(result?.Message ?? "Blueprint refreshed.");
    } catch (err: any) {
      this.notificationService.error(`Failed to refresh: ${err?.message || err}`);
    } finally {
      this.isRefreshing = false;
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  private async loadHistory(serviceTreeId: string, type: string, name: string): Promise<void> {
    this.isLoading = true;
    this.loadingService.setLoading(true);
    try {
      const versions = await this.apiService.getBlueprintHistory(serviceTreeId, type, name).toPromise();
      if (!versions) {
        this.errorMessage = "Request returned no data.";
        return;
      }
      this.errorMessage = null;
      this.applyResult(versions, { ServiceTreeId: serviceTreeId, Type: type, Name: name });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      this.errorMessage = `Failed to load blueprint: ${message}`;
      this.notificationService.error(this.errorMessage);
    } finally {
      this.isLoading = false;
      this.loadingService.setLoading(false);
      this.cdr.detectChanges();
    }
  }

  private applyResult(versions: Blueprint[], lineage: BlueprintLineage): void {
    this.lineage = lineage;
    this.rowData = versions.map((v) => ({ ...(v as unknown as Record<string, unknown>) }));
    this.gridOptions = this.buildGridOptions();
  }

  private buildGridOptions(): GridOptions<Record<string, unknown>> {
    return extendDefaultOptions({
      columnDefs: this.blueprintColumns(),
      paginationPageSize: this.initPageSize,
      rowSelection: "single",
      animateRows: true,
      getRowClass: (params) => (params.data?.Id === this.entityId ? "current-version-row" : ""),
    });
  }

  /** Deep link to the versioned Bicep source in Azure DevOps. */
  private buildBicepFileUrl(commitHash: string | null | undefined): string | null {
    if (!commitHash || !this.lineage) {
      return null;
    }
    const repo = environment.isTestEnv ? "Azure-CIS-CapacityBlueprints-Test" : "Azure-CIS-CapacityBlueprints";
    const path = `/blueprints/${this.lineage.ServiceTreeId}/${this.lineage.Type}/${this.lineage.Name}.bicep`;
    return `https://msazure.visualstudio.com/One/_git/${repo}?path=${path}&version=GC${commitHash}`;
  }

  /** Logical-key deep link to this blueprint's version history. */
  private buildBlueprintDetailsUrl(blueprintId: number | null | undefined): string | null {
    if (!this.lineage || blueprintId == null) {
      return null;
    }
    const { ServiceTreeId, Type, Name } = this.lineage;
    return `/quota/capacity/blueprint/serviceTreeId/${encodeURIComponent(ServiceTreeId)}/type/${encodeURIComponent(Type)}/name/${encodeURIComponent(Name)}?ID=${blueprintId}`;
  }

  private blueprintColumns(): ColDef[] {
    return [
      {
        headerName: "Id",
        field: "Id",
        filter: "agNumberColumnFilter",
        width: 100,
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          target: "_blank",
          url: (params: { data?: { Id?: number } }) => this.buildBlueprintDetailsUrl(params.data?.Id),
        },
      },
      {
        headerName: "Commit Hash",
        field: "CommitHash",
        filter: "agTextColumnFilter",
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          target: "_blank",
          text: (params: any) => (params.value ? String(params.value).substring(0, 8) : ""),
          url: (params: any) => this.buildBicepFileUrl(params.data?.CommitHash),
        },
      },
      { headerName: "Owning Team", field: "OwningTeam", filter: "agTextColumnFilter" },
      { headerName: "Source", field: "Source", filter: "agTextColumnFilter" },
      { headerName: "Last Updated By", field: "LastUpdatedBy", filter: "agTextColumnFilter" },
      {
        headerName: "Created Time (UTC)",
        field: "CreatedTime",
        cellRenderer: (params: any) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (a: any, b: any) => agDateColumnFilter(a, b),
        },
      },
    ];
  }
}
